import React, { memo, useEffect, useState } from 'react';
import Button from '@components/atoms/button';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type?: 'no-updates' | 'email-only';
  testId?: string;
}

const CONTENT_LOOKUP = {
  'no-updates': {
    title: "Are you sure you don't want to receive updates?",
    message:
      'Our AI-powered insights helped Fantasy Football Hub members win over <strong>50,000</strong> mini-leagues last season. You can unsubscribe anytime.',
    colorClasses: 'bg-amber-50 border-amber-500 text-amber-700',
    confirmButtonText: 'Continue without updates',
    cancelButtonText: 'Go back'
  },
  'email-only': {
    title: "Are you sure you don't want instant updates?",
    message:
      'Enabling push notifications is the <strong>best way</strong> to stay ahead of your rivals. Get everything you need to win, the <strong>moment</strong> it happens. Crucial injury updates, price changes, and team news delivered instantly.',
    colorClasses: 'bg-blue-50 border-blue-500 text-blue-700',
    confirmButtonText: 'Continue with email only',
    cancelButtonText: 'Enable push'
  }
} as const;

/**
 * A modal component for confirmation dialogs
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type = 'no-updates',
  testId = 'confirmation-modal'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      // Small delay to ensure DOM is ready before starting animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setIsRendered(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isRendered) return null;

  const content = CONTENT_LOOKUP[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        data-testid={testId}
        className={`fixed left-1/2 top-1/2 w-[95vw] sm:w-[90vw] max-w-xs sm:max-w-md bg-white rounded-lg shadow-lg focus:outline-none transform -translate-x-1/2 -translate-y-1/2 p-4 sm:p-6 transition-all duration-300 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          {content.title}
        </h2>
        <div
          className={`mt-3 sm:mt-4 border-l-4 rounded p-4 sm:p-6 ${content.colorClasses}`}
        >
          <p
            className="text-xs sm:text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.message }}
          />
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end w-full mt-4 sm:mt-5 gap-2 sm:gap-3">
          <Button
            variant="secondary"
            onClick={onConfirm}
            className="text-xs sm:text-sm font-medium py-2 sm:py-2.5 px-3 sm:px-4 hover:bg-gray-200 transition-colors w-full sm:w-auto"
          >
            {content.confirmButtonText}
          </Button>
          <Button
            variant="primary"
            onClick={onClose}
            className="text-xs sm:text-sm font-medium py-2 sm:py-2.5 px-3 sm:px-4 hover:bg-blue-700 transition-colors w-full sm:w-auto"
          >
            {content.cancelButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

ConfirmationModal.displayName = 'ConfirmationModal';

export default memo(ConfirmationModal);
